import axios from 'axios'
import authHeader from './auth-header'
import { API_BASE_URL } from '@/config/api.config'

class ApiService {
  // Equipment endpoints

  /**
   * @param {{ object?: number, company?: number, status?: string, date?: string }} [params]
   * @returns {Promise}
   */
  getEquipments(params = {}) {
    return axios.get(`${API_BASE_URL}/monitor/equipments/`, { headers: authHeader(), params })
  }

  /** @param {number} id @returns {Promise} */
  getEquipment(id) {
    return axios.get(`${API_BASE_URL}/monitor/equipments/${id}/`, { headers: authHeader() })
  }

  /** @param {number} id @param {{ from?: string, to?: string, period?: string }} [params] @returns {Promise} */
  getEquipmentMetrics(id, params = {}) {
    return axios.get(`${API_BASE_URL}/monitor/equipments/${id}/metrics/`, { headers: authHeader(), params })
  }

  /** Периодичность отображения данных VKT (15/30/60 минут) */
  setVktPollInterval(id, minutes) {
    return axios.patch(
      `${API_BASE_URL}/monitor/equipments/${id}/vkt-poll-interval/`,
      { poll_interval_minutes: minutes },
      { headers: authHeader() }
    )
  }

  getEquipmentStatistics(id, period = 'day') {
    return axios.get(`${API_BASE_URL}/monitor/equipments/${id}/statistics/`, {
      headers: authHeader(),
      params: { period },
    })
  }

  /** @param {number} id @param {boolean|null} [isResolved] @returns {Promise} */
  getEquipmentAlerts(id, isResolved = null) {
    const params = {}
    if (isResolved !== null) params.is_resolved = isResolved
    return axios.get(`${API_BASE_URL}/monitor/equipments/${id}/alerts/`, { headers: authHeader(), params })
  }

  /** @param {number} id @returns {Promise} */
  getLatestParameters(id) {
    return axios.get(`${API_BASE_URL}/monitor/equipments/${id}/latest_parameters/`, { headers: authHeader() })
  }

  // Map endpoints
  getEquipmentMap(params = {}) {
    return axios.get(`${API_BASE_URL}/monitor/map/equipment/`, { headers: authHeader(), params })
  }

  getEquipmentMapMetrics(cityId = null) {
    const params = cityId ? { city_id: cityId } : {}
    return axios.get(`${API_BASE_URL}/monitor/equipments/latest_metrics_map/`, { headers: authHeader(), params })
  }

  // GeoJSON endpoints
  getGeoJson(params = {}) {
    return axios.get(`${API_BASE_URL}/geojson/`, { headers: authHeader(), params })
  }

  getGeoJsonEquipment(geojsonId) {
    return axios.get(`${API_BASE_URL}/geojson/${geojsonId}/equipment/`, { headers: authHeader() })
  }

  // Alerts endpoints

  /** @param {{ equipment?: number, is_resolved?: boolean }} [params] @returns {Promise} */
  getAlerts(params = {}) {
    return axios.get(`${API_BASE_URL}/monitor/alerts/`, { headers: authHeader(), params })
  }

  getActiveAlerts() {
    return axios.get(`${API_BASE_URL}/monitor/alerts/active/`, { headers: authHeader() })
  }

  /** @param {number} id @returns {Promise} */
  resolveAlert(id) {
    return axios.post(`${API_BASE_URL}/monitor/alerts/${id}/resolve/`, {}, { headers: authHeader() })
  }

  // Objects endpoints
  getObjects(params = {}) {
    return axios.get(`${API_BASE_URL}/monitor/objects/`, { headers: authHeader(), params })
  }

  getObject(id) {
    return axios.get(`${API_BASE_URL}/monitor/objects/${id}/`, { headers: authHeader() })
  }

  // Parameter types endpoints
  getParameterTypes() {
    return axios.get(`${API_BASE_URL}/monitor/parameter-types/`, { headers: authHeader() })
  }

  // Object types endpoints
  getObjectTypes() {
    return axios.get(`${API_BASE_URL}/monitor/object-types/`, { headers: authHeader() })
  }

  // Metrics endpoints
  getMetrics(params = {}) {
    return axios.get(`${API_BASE_URL}/monitor/metrics/`, { headers: authHeader(), params })
  }

  // User endpoints
  getUserProfile() {
    return axios.get(`${API_BASE_URL}/users/me/`, { headers: authHeader() })
  }

  /** @param {string} oldPassword @param {string} newPassword @returns {Promise} */
  changePassword(oldPassword, newPassword) {
    return axios.post(`${API_BASE_URL}/users/change_password/`, {
      old_password: oldPassword,
      new_password: newPassword,
    }, { headers: authHeader() })
  }

  // Weather endpoints
  getCurrentWeather() {
    return axios.get(`${API_BASE_URL}/weather/current/`, { headers: authHeader() })
  }

  getStationWeather() {
    return axios.get(`${API_BASE_URL}/weather/station-data/`, { headers: authHeader() })
  }

  getStationTemperatureSeries(params = {}) {
    return axios.get(`${API_BASE_URL}/weather/station-history/`, { headers: authHeader(), params })
  }

  getApiWeather() {
    return axios.get(`${API_BASE_URL}/weather/api/`, { headers: authHeader() })
  }

  getWeatherWeeklyStats(source = null) {
    const params = source ? { source } : {}
    return axios.get(`${API_BASE_URL}/weather/weekly-stats/`, { params, headers: authHeader() })
  }

  getMonthlyTemperatureReport(year, month, cityId = null) {
    const params = { year, month }
    if (cityId) params.city_id = cityId
    return axios.get(`${API_BASE_URL}/weather/monthly-report/`, {
      params,
      headers: authHeader(),
    })
  }

  // Reports endpoints

  /** @param {number} year @param {number} month @param {number|null} [cityId] @returns {Promise} */
  getHeatingNetworkReport(year, month, cityId = null) {
    const params = { year, month }
    if (cityId) params.city_id = cityId
    return axios.get(`${API_BASE_URL}/monitor/reports/heating_network_report/`, {
      params,
      headers: authHeader(),
    })
  }

  /** @param {number} year @param {number} month @returns {Promise} */
  getEngineerReport(year, month) {
    return axios.get(`${API_BASE_URL}/monitor/reports/engineer_report/`, {
      headers: authHeader(),
      params: { year, month },
    })
  }

  /** @param {number} temperature - температура наружного воздуха (°C) @param {number|null} [cityId] @returns {Promise} */
  getEngineerReportByTemperature(temperature, cityId = null) {
    const params = { temperature }
    if (cityId) params.city_id = cityId
    return axios.get(`${API_BASE_URL}/monitor/reports/engineer_report_by_temperature/`, {
      headers: authHeader(),
      params,
    })
  }

  getBIDashboardFrame1() {
    return axios.get(`${API_BASE_URL}/monitor/reports/bi_dashboard_frame1/`, { headers: authHeader() })
  }

  getBIDashboardFrame2(frameId) {
    return axios.get(`${API_BASE_URL}/monitor/reports/bi_dashboard_frame2/${frameId}/`, { headers: authHeader() })
  }

  getBIDashboardFrame3(frameId) {
    return axios.get(`${API_BASE_URL}/monitor/reports/bi_dashboard_frame3/${frameId}/`, { headers: authHeader() })
  }

  // PROLOG Boiler Data endpoints
  getBoilerHeatLatest() {
    return axios.get(`${API_BASE_URL}/prolog/boiler-data/heat/latest/`, { headers: authHeader() })
  }

  getBoilerHeatAvg24h() {
    return axios.get(`${API_BASE_URL}/prolog/boiler-data/heat/avg24h/`, { headers: authHeader() })
  }

  getBoilerGasLatest() {
    return axios.get(`${API_BASE_URL}/prolog/boiler-data/gas/latest/`, { headers: authHeader() })
  }

  getBoilerGasAvg24h() {
    return axios.get(`${API_BASE_URL}/prolog/boiler-data/gas/avg24h/`, { headers: authHeader() })
  }

  getBoilerCombinedReport() {
    return axios.get(`${API_BASE_URL}/prolog/boiler-data/combined/report/`, { headers: authHeader() })
  }

  getTomorrowForecast() {
    return axios.get(`${API_BASE_URL}/weather/forecast/tomorrow/`, { headers: authHeader() })
  }

  // Region config

  /** @param {number|null} [cityId] @returns {Promise} */
  getRegionConfig(cityId = null) {
    const params = cityId ? { city_id: cityId } : {}
    return axios.get(`${API_BASE_URL}/config/region/`, { headers: authHeader(), params })
  }

  getUserCities() {
    return axios.get(`${API_BASE_URL}/config/user-cities/`, { headers: authHeader() })
  }

  /** @param {number|null} [cityId] @returns {Promise} */
  hasWeatherStation(cityId = null) {
    const params = cityId ? { city_id: cityId } : {}
    return axios.get(`${API_BASE_URL}/weather/has-station/`, { headers: authHeader(), params })
  }
}

export default new ApiService()
